import { type Flipper, type Flips } from 'legacy/contexts/flipper/FlipperContext'
import { type Permission } from 'legacy/common/types'
import { type WarehouseLocationFeature } from 'legacy/common/warehouseLocations'

type AccessControlRequiredValues<T> = T | AccessControlOneOf<T> | Array<T | AccessControlOneOf<T>>

export type AccessControlFlippers<T = Flipper> = AccessControlRequiredValues<T>
export type AccessControlPermissions<T = Permission> = AccessControlRequiredValues<T>
export type AccessControlWarehouseLocationFeatures<T = WarehouseLocationFeature> =
  AccessControlRequiredValues<T>

export interface AccessControlConfig<
  TFlipper = Flipper,
  TPermission = Permission,
  TWarehouseLocationFeature = WarehouseLocationFeature
> {
  flippers?: AccessControlFlippers<TFlipper>
  notFlippers?: AccessControlFlippers<TFlipper>
  warehouseFlippers?: AccessControlFlippers<TFlipper>
  notWarehouseFlippers?: AccessControlFlippers<TFlipper>
  permissions?: AccessControlPermissions<TPermission>
  warehouseLocationFeatures?: AccessControlWarehouseLocationFeatures<TWarehouseLocationFeature>
}

export class AccessControlOneOf<T> {
  values: T[]

  constructor(values: T[]) {
    this.values = values
  }

  toJSON() {
    return { oneOf: this.values }
  }
}

function hasValues<T>(values: T[], requiredValues: AccessControlRequiredValues<T>): boolean {
  if (requiredValues instanceof AccessControlOneOf) {
    return requiredValues.values.reduce<boolean>(
      (result, value) => result || hasValues(values, value),
      false
    )
  }

  if (Array.isArray(requiredValues)) {
    return requiredValues.reduce<boolean>(
      (result, value) => hasValues(values, value) && result,
      true
    )
  }

  return values.includes(requiredValues)
}

export function flippersToFlipsMapping(flippers?: Flipper[]) {
  if (!flippers) {
    return {}
  }

  return flippers.reduce((flips: Flips, flipper) => ({ ...flips, [flipper]: true }), {})
}

export function hasFlipper(flippers: Flips, requiredFlips: AccessControlFlippers): boolean {
  const flips = (Object.keys(flippers) as Flipper[]).filter(n => flippers[n])

  return hasValues(flips, requiredFlips)
}

export function hasPermission(
  permissions: Permission[],
  requiredPermissions: AccessControlPermissions
): boolean {
  return hasValues(permissions, requiredPermissions)
}

export function hasWarehouseLocationFeature(
  features: WarehouseLocationFeature[],
  requiredFeatures: AccessControlWarehouseLocationFeatures
): boolean {
  return hasValues(features, requiredFeatures)
}
